import {
    ApiCadAdditionalInfo,
    BoloAdditionalInfo,
    CadNextNotificationBundle,
    EventQueueWireDto,
    NudgeObjectDto,
    PhoneCallDto,
    ResponsePlanNotification,
    ScheduledEventQueueView,
    UnitQueueWireDto,
    ExportsStreamMessageTypeEnumType,
    UnitGpsDto,
    HospitalQueueView,
    UserAssignmentBundle,
    EventPanelView,
    UpdateUnitQueueBundle,
    UpdateEventQueueBundle,
    EventQueuesBundle,
    UnitQueuesBundle,
    BoloQueueView,
    BoloQueuesBundle,
    BoloPanelView,
    UnitEmergencyDto,
    MapViewBundle,
    FederatedSearchQueryBundle,
    UnitLocationBootstrapResponse,
    FederatedSearchUserQueryMetadata,
    RestartNotificationResponse,
    AmlPhoneCallDto,
    BasicAttachmentDto,
    ManageUnitsUnitView,
    ResponsePlanV2,
    SensorAlertBundle,
} from '@mark43/cad-api';

import { RawQueueRowData } from '../queues/store/types-2';

export type CautionLevelAttrId = 222000 | 222001 | 222002 | 222003;

export type EventQueueRowData = Partial<EventQueueWireDto> & RawQueueRowData;

// remove Omit in https://mark43.atlassian.net/browse/CAD-22042
export type UnitQueueRowData = Partial<Omit<UnitQueueWireDto, 'backingUpUnitId'>> &
    RawQueueRowData & {
        // dummy properties to satisfy typings of priority-cell
        assignedUnits?: undefined;
        maxUnassignedTimeReached?: undefined;
    };

export type ScheduledEventQueueRowData = RawQueueRowData & Partial<ScheduledEventQueueView>;

export type HospitalQueueRowData = RawQueueRowData & Partial<HospitalQueueView>;

export type BoloQueueRowData = RawQueueRowData & Partial<BoloQueueView>;

export const ProQaNotificationSeverities = {
    DEFAULT: 'default',
    ERROR: 'error',
} as const;

export type ProQaNotificationSeverity =
    (typeof ProQaNotificationSeverities)[keyof typeof ProQaNotificationSeverities];

type BaseProQaEventsMessage = {
    actingUserId: number;
    departmentId: number;
    correlationId?: string;
};

export type ProQaSecondaryEventPanelMetadata = {
    eventId: number;
    parentAgencyEventId: number;
    incidentNumber?: string;
    overIncidentNumber?: string;
    targetCadDiscipline: string;
};

type ProQaOpenSecondaryEventPanelsMessage = BaseProQaEventsMessage & {
    action: 'OPEN_SECONDARY_EVENT_PANELS';
    metadata: {
        secondaryEvents: ProQaSecondaryEventPanelMetadata[];
        shouldFocusPanel?: boolean;
    };
};

type ProQaShowNotificationMessage = BaseProQaEventsMessage & {
    action: 'SHOW_NOTIFICATION';
    metadata: {
        message?: string;
        severity?: ProQaNotificationSeverity;
    };
};

export type ProQaEventsMessage =
    | ProQaOpenSecondaryEventPanelsMessage
    | ProQaShowNotificationMessage;

/**
 * Push delivered on the `responsePlanGenerated` stream after the FE kicks off async response-plan
 * generation via `POST manage_units/generate_response_plan`. This is NOT a `@mark43/cad-api`
 * generated model — the BE message (`ResponsePlanGeneratedMessage`, a pub/sub POJO) is never exposed
 * over REST, so the wire shape is hand-authored here and kept in lockstep with the BE.
 *
 * `clientActionId` echoes the `x-cad-client-action-id` header sent on the kick request; the consumer
 * matches it against the uuid it minted so a stale push (from an earlier kick) can be dropped.
 * `failed` is `true` when the worker timed out or errored — in that case the plan fields are absent.
 *
 * `departmentId` / `actingUserId` ride down from the BE `BaseMessage` envelope and are included to
 * mirror the wire shape, matching how other hand-authored StreamMap messages (e.g. ProQa) carry
 * their base envelope fields.
 */
export type ResponsePlanGeneratedMessage = {
    departmentId: number;
    actingUserId?: number;
    eventId: number;
    clientActionId?: string;
    initiallySelectedUnits: ManageUnitsUnitView[];
    responsePlanV2?: ResponsePlanV2;
    initialPrimaryUnitId?: number;
    failed: boolean;
};

/**
 * A map from the name of a stream to the shape of payload for message
 */
export type StreamMap = {
    nudge: NudgeObjectDto;
    infos: {
        reset?: number[];
        create?: (ApiCadAdditionalInfo & { text?: string })[];
        delete?: { agencyEventId: number; id: number }[];
        update?: (ApiCadAdditionalInfo & { text?: string })[];
    };
    boloAdditionalInfo: {
        boloId: number;
        boloLinkId: number;
        boloAdditionalInfo: BoloAdditionalInfo;
    };
    serviceRotationNotifications: {
        agencyEventId: number;
        departmentId: number;
    };
    cadNextPhoneCalls: PhoneCallDto;
    cadNextNotifications: CadNextNotificationBundle;
    proqa: ProQaEventsMessage;
    cadNextAml: { context: AmlPhoneCallDto };
    dex_nudge: number;
    responsePlanNotifications: ResponsePlanNotification;
    externalActionNotifications: {
        message: string;
        severity: 'DEFAULT' | 'ERROR';
        createdByUserId: number;
    };
    cadExports: { ids: string[]; exportsStreamMessageType: ExportsStreamMessageTypeEnumType };
    dexInquiryFailedNotifications: {
        userId: number;
        message: string;
    };
    gps: UnitGpsDto;
    userAssignment: UserAssignmentBundle;
    eventPanelUpdates: {
        eventPanelViews: EventPanelView[];
    };
    updateUnitQueue: UpdateUnitQueueBundle;
    updateEventQueue: UpdateEventQueueBundle;
    eventQueues: EventQueuesBundle;
    unitQueues: UnitQueuesBundle;
    boloQueues: BoloQueuesBundle;
    boloPanelView: BoloPanelView;
    unitEmergencyDto: UnitEmergencyDto;
    mapViews: MapViewBundle;
    mapViewsNoCjis: MapViewBundle;
    unitLocationBundles: UnitLocationBootstrapResponse;
    federatedSearchUserQueryMetadata: FederatedSearchUserQueryMetadata;
    userFederatedSearchQueryBundles: FederatedSearchQueryBundle;
    eventFederatedSearchQueryBundles: FederatedSearchQueryBundle;
    restartApplicationNotifications: RestartNotificationResponse;
    cadAttachments: {
        attachments: BasicAttachmentDto[];
    };
    responsePlanGenerated: ResponsePlanGeneratedMessage;
    sensorAlert: SensorAlertBundle;
};
